/**
 * Images for the editor's previews (spec 009): media come through the
 * authenticated client (`getMediaFileBlob`) and are decoded once per media id
 * and kept for the session, so a second Preview starts without a fetch.
 */

import type { SceneImages } from '../../../Shared/src/runtime/SceneRuntime';
import { sceneImageIds } from '../../../Shared/src/runtime/browserMedia';
import type { PlayableScene } from '../../../Shared/src/@types';
import { registerSessionReset } from '../api/session';

export interface MediaBlobSource {
  getMediaFileBlob(mediaId: number): Promise<Blob>;
}

const decoded = new Map<number, Promise<HTMLImageElement | null>>();
const ready = new Map<number, HTMLImageElement | null>();

/** A media URL the preview runtimes hand back to `loadPreviewImageUrl`. */
export const previewMediaUrl = (mediaId: number) => `sm-media:${mediaId}`;

function decode(blob: Blob): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };
    img.src = url;
  });
}

/** Decode media `id` (cached); resolves null when it cannot be fetched or decoded. */
export function loadPreviewImage(media: MediaBlobSource, mediaId: number): Promise<HTMLImageElement | null> {
  let p = decoded.get(mediaId);
  if (!p) {
    p = media.getMediaFileBlob(mediaId)
      .then(decode, () => null)
      .then((img) => {
        ready.set(mediaId, img);
        if (!img) decoded.delete(mediaId); // a later preview may try again
        return img;
      });
    decoded.set(mediaId, p);
  }
  return p;
}

/** `loadImage` for `StoryRuntime`: takes a `previewMediaUrl`. */
export function loadPreviewImageUrl(media: MediaBlobSource, url: string): Promise<HTMLImageElement | null> {
  const id = Number(url.replace(/^sm-media:/, ''));
  return Number.isFinite(id) && id > 0 ? loadPreviewImage(media, id) : Promise.resolve(null);
}

type ImageScene = Pick<PlayableScene, 'sprites' | 'background'>;

/** The scene's images that are decoded already (no fetch). */
export function cachedSceneImages(scene: ImageScene): { images: SceneImages; missing: number[] } {
  const images: SceneImages = {};
  const missing: number[] = [];
  for (const id of sceneImageIds(scene)) {
    const img = ready.get(id);
    if (img) images[id] = img;
    else if (!ready.has(id)) missing.push(id);
  }
  return { images, missing };
}

/** Decode every image of the scene, keyed by media id; failures are left out. */
export async function loadSceneImagesForPreview(media: MediaBlobSource, scene: ImageScene): Promise<SceneImages> {
  const ids = sceneImageIds(scene);
  const imgs = await Promise.all(ids.map((id) => loadPreviewImage(media, id)));
  const images: SceneImages = {};
  ids.forEach((id, i) => {
    const img = imgs[i];
    if (img) images[id] = img;
  });
  return images;
}

type ImageListener = (mediaId: number) => void;
const listeners = new Set<ImageListener>();

/** Called with a media id whose file changed (spec 027: a generated picture's full-size redraw). */
export function onPreviewImageChanged(listener: ImageListener): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

/**
 * Re-fetches media `id` (its file changed on the server) and swaps the
 * decoded image in the cache, so open previews and thumbnails show it
 * without a page reload.
 */
export async function refreshPreviewImage(media: MediaBlobSource, mediaId: number): Promise<void> {
  let img: HTMLImageElement | null = null;
  try {
    img = await decode(await media.getMediaFileBlob(mediaId));
  } catch {
    img = null;
  }
  if (!img) return;
  decoded.set(mediaId, Promise.resolve(img));
  ready.set(mediaId, img);
  for (const l of listeners) l(mediaId);
}

/** Forget every decoded image (sign-out). */
export function clearPreviewImages(): void {
  decoded.clear();
  ready.clear();
}

registerSessionReset(clearPreviewImages);
